import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import InputFormik from "@/common/components/form/InputFormik";
import { Modal } from "flowbite-react";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { ICampaign } from "@/types/campaign.type";
import { useCreateCampaign, useUpdateCampaign, useMarketingMessageTemplates, useGetCompanies } from "@/common/hooks";
import SelectFormik from "@/common/components/form/SelectFormik";
import { useEffect, useState } from "react";
import CustomSelect from "@/modules/renters/components/forms/fields/CustomSelect";
import { FINANCIAL_STATUS, RENTER_STATUS } from "@/types/renter.type";

interface FormCampaignProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: ICampaign;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required("O nome é obrigatório"),
  date: Yup.string().required("A data e hora de envio são obrigatórias"),
  model: Yup.string().required("O modelo da mensagem é obrigatório"),
  audienceType: Yup.string().required("O tipo de público é obrigatório"),
  targetAudience: Yup.array(Yup.string()).when('audienceType', {
    is: (val) => val,
    then: (schema) => schema.min(1, "Selecione ao menos uma opção"),
    otherwise: (schema) => schema,
  }),
  renterStatus: Yup.array(Yup.string()).when('audienceType', {
    is: 'renters',
    then: (schema) => schema.min(1, "Selecione ao menos um status"),
    otherwise: (schema) => schema,
  }),
  renterSituation: Yup.array(Yup.string()).when('audienceType', {
    is: 'renters',
    then: (schema) => schema.min(1, "Selecione ao menos uma situação"),
    otherwise: (schema) => schema,
  }),
  description: Yup.string(),
});

const audiencteType = {
	leads: 'leads', 
	renters: 'renters'
}

const audienceTypeOptions = [
  { value: 'leads', label: 'Leads' },
  { value: 'renters', label: 'Locatários' },
];

export function FormCampaign({
  isOpen,
  onClose,
  initialData,
}: FormCampaignProps) {
  const { mutate: createCampaign, isPending: isCreating } = useCreateCampaign();
  const { mutate: updateCampaign, isPending: isUpdating } = useUpdateCampaign();
  const { data: messageTemplatesData } = useMarketingMessageTemplates();
  const { data: companiesData = [] } = useGetCompanies();

  const loadCompanyOptions = companiesData
    .map((company) =>
      ({ label: company.address.city, value: company.address.city })
    )

  const [messageTemplateOptions, setMessageTemplateOptions] = useState<string[]>([]);

  useEffect(() => {
    if (messageTemplatesData) {
      const options = messageTemplatesData?.pages?.flatMap(page =>
        page.data.map((template) => template.name)
      );
      setMessageTemplateOptions(options);
    }
  }, [messageTemplatesData]);

  const handleSubmit = (values: Omit<ICampaign, '_id' | 'sentCount' | 'status'>) => {
    if (initialData) {
      updateCampaign({ id: initialData._id, data: values }, { onSuccess: onClose });
    }
    else {
      createCampaign(values, { onSuccess: onClose });
    }
  };

  const SITUATION_FINANCIAL_LABELS = {
    [FINANCIAL_STATUS.INADIMPLENTE]: "Inadimplente",
    [FINANCIAL_STATUS.ADIMPLENTE]: "Adimplente",
  };

  const RENTER_STATUS_LABELS = {
    [RENTER_STATUS.Ativo]: "Ativo",
    [RENTER_STATUS.Bloqueado]: "Bloqueado",
    [RENTER_STATUS.Congelado]: "Congelado",
    [RENTER_STATUS.Finished]: "Finalizado",
    [RENTER_STATUS.Incompleto]: "Incompleto",
    [RENTER_STATUS.Indefinido]: "Indefinido",
    [RENTER_STATUS.PendentePagamento]: "Pendente Pagamento",
    [RENTER_STATUS.ProntoParaAprovacao]: "Pronto para Aprovação",
    [RENTER_STATUS.Recall]: "Recolhimento",
    [RENTER_STATUS.Recision]: "Rescisão",
    [RENTER_STATUS.ReservaPaga]: "Reserva Paga",
    [RENTER_STATUS.ReservaPendentePagamento]: "Reserva Pendente Pagamento",
    [RENTER_STATUS.WaitingRescision]: "Aguardando Rescisão",
    [RENTER_STATUS.Blacklist]: "Blacklist",
  }

  return (
    <Modal show={isOpen} onClose={onClose} className="relative">
      <Modal.Header>
        <h2 className="relative dark:text-black">
          {initialData ? "Editar Campanha" : "Nova Campanha"}
        </h2>
      </Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={{
            name: initialData?.name || "",
            date: initialData?.date || "",
            description: initialData?.description || "",
            model: initialData?.model || "",
            audienceType: initialData?.audienceType || "",
            targetAudience: initialData?.targetAudience || [],
            renterStatus: initialData?.renterStatus || [],
            renterSituation: initialData?.renterSituation || [],
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values }) => (
            <Form>
              <div className="p-6.5">
                <div className="mb-4.5">
                  <InputFormik label="Nome da Campanha" name="name" type="text" />
                </div>

                <div className="mb-4.5">
                  <InputFormik label="Descrição" name="description" type="text" />
                </div>

                <div className="mb-4.5">
                  <SelectFormik label="Modelo da Mensagem" name="model" options={messageTemplateOptions} />
                </div>

                <div className="mb-4.5 pb-4">
                  <InputFormik label="Data e Hora do Envio" name="date" type="datetime-local"/>
                </div>

                <div className="mb-4.5 pb-4">
                  <Field
                    name="audienceType"
                    component={CustomSelect}
                    options={audienceTypeOptions}
                    placeholder="Tipo de Público"
                  />
                </div>
                {(values.audienceType === audiencteType.leads || values.audienceType === audiencteType.renters) && (
                  <div className="mb-4.5 pb-4">
                    <Field
                      name="targetAudience"
                      component={CustomSelect}
                      options={loadCompanyOptions}
                      isMulti={true}
                      placeholder="Selecione a(s) cidade(s)"
                    />
                  </div>
                )}

                {values.audienceType === audiencteType.renters && (
                  <>
                    <div className="mb-4.5 pb-4">
                      <Field
                        name="renterStatus"
                        component={CustomSelect}
                        options={Object.entries(RENTER_STATUS_LABELS).map(([value, label]) => ({ label, value }))}
                        isMulti={true}
                        placeholder="Selecione o(s) status do locatário"
                      />
                    </div>
                    <div className="mb-4.5">
                      <Field
                        name="renterSituation"
                        component={CustomSelect}
                        options={Object.entries(SITUATION_FINANCIAL_LABELS).map(([value, label]) => ({ label, value }))}
                        isMulti={true}
                        placeholder="Selecione a(s) situação(ões) financeira(s)"
                      />
                    </div>
                  </>
                )}


                <ButtonWithLoader
                  type="submit"
                  isLoading={isCreating || isUpdating}
                >
                  Salvar
                </ButtonWithLoader>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal >
  );
}

